// ═══════════════════════════════════════════════════════════════
// 📘 MÓDULO 9 - UNIDAD 6: QUESTION TAGS - ASKING FOR AGREEMENT
// ═══════════════════════════════════════════════════════════════
// ✅ Biblia v4.1 - 10 SETS de 10 PREGUNTAS
// ✅ Context = SITUACIÓN NARRATIVA (NO gramática)
// ✅ Progresión: A2+ → B2+
// ✅ Topic: Travel & Leisure Plans
// ✅ Matches con significado al inicio de cada set
// ═══════════════════════════════════════════════════════════════

export const M9_UNIT_6 = {
  id: "m9-u6-question-tags",
  title: "Unidad 6: Question Tags - Asking for Agreement",
  topic: "Master question tags to check information, seek agreement, and sound more natural in English conversations about travel, leisure activities, and everyday plans.",
  iconType: "message-circle",

  sets: [
    // ───────────────────────────────────────────────────────────
    // SET 1: Travel Agency - Present Simple Tags (A2+)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u6-s1",
      title: "Set 1: Travel Agency Basics",
      icon: "✈️",
      context: `**SITUACIÓN:** You're at a travel agency in Montreal booking a summer vacation package to Vancouver. The agent is confirming details about flights, hotels, and activities, and you need to check information using question tags to make sure everything is correct before paying.`,

      questions: [
        {
          type: "match",
          q: "**Match the statement → correct question tag**",
          pairs: [
            { left: "The flight leaves at 9am", right: "doesn't it?" },
            { left: "You don't like window seats", right: "do you?" },
            { left: "Vancouver has beautiful parks", right: "doesn't it?" },
            { left: "We need passports", right: "don't we?" }
          ],
          successTip: "💡 **Pattern mastered!** Positive statement = negative tag. This is how native speakers confirm info!"
        },
        {
          type: "mc",
          q: "**Agent:** 'The package includes breakfast, ___ ?'",
          options: ["doesn't it", "does it", "isn't it", "don't it"],
          a: "doesn't it",
          successTip: "🎯 **Perfect!** 'includes' (present simple positive) → 'doesn't it?' (negative tag)"
        },
        {
          type: "mc",
          q: "**You:** 'The hotel offers free WiFi, ___ ?'",
          options: ["doesn't it", "does it", "isn't it", "hasn't it"],
          a: "doesn't it",
          successTip: "📶 **Excellent!** Checking amenities with tags sounds professional worldwide!"
        },
        {
          type: "input",
          q: "**Agent:** 'You prefer mountain views, ___ ___ ?'",
          alts: ["don't you"],
          a: "don't you",
          successTip: "🏔️ **Natural!** 'prefer' → 'don't you?' - confirming preferences politely!"
        },
        {
          type: "mc",
          q: "**You:** 'The trip costs $2,500, ___ ?'",
          options: ["doesn't it", "does it", "isn't it", "don't it"],
          a: "doesn't it",
          successTip: "💰 **Right!** 'costs' (verb) → 'doesn't it?' - financial confirmation essential!"
        },
        {
          type: "mc",
          q: "**Agent:** 'You don't want travel insurance, ___ ?'",
          options: ["do you", "don't you", "are you", "aren't you"],
          a: "do you",
          successTip: "🛡️ **Correct!** Negative 'don't want' → positive tag 'do you?'"
        },
        {
          type: "input",
          q: "**You:** 'The flight stops in Calgary, ___ ___ ?'",
          alts: ["doesn't it"],
          a: "doesn't it",
          successTip: "🛬 **Perfect!** Layover confirmation - crucial for travel planning!"
        },
        {
          type: "mc",
          q: "**Agent:** 'Your friend travels with you, ___ ?'",
          options: ["doesn't she", "does she", "isn't she", "hasn't she"],
          a: "doesn't she",
          successTip: "👥 **Great!** Third person 'travels' → 'doesn't she?' Pattern consistent!"
        },
        {
          type: "mc",
          q: "**You:** 'We need to check in online, ___ ?'",
          options: ["don't we", "do we", "aren't we", "won't we"],
          a: "don't we",
          successTip: "💻 **Excellent!** Modern travel requirement confirmed with tag!"
        },
        {
          type: "order",
          q: "**Say:** 'Los vuelos salen los martes, ¿verdad?'",
          words: ["The", "flights", "leave", "on", "Tuesdays", "don't", "they"],
          extra: ["doesn't", "do", "aren't"],
          a: ["The", "flights", "leave", "on", "Tuesdays", "don't", "they"],
          successTip: "🗓️ **Perfecto!** Plural 'flights leave' → 'don't they?' - schedule confirmation!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // SET 2: Packing for Vacation - Past Simple Tags (A2+)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u6-s2",
      title: "Set 2: Packing Memories",
      icon: "🧳",
      context: `**SITUACIÓN:** You're packing for your Vancouver trip with your roommate. You're reminiscing about last year's trip to Toronto and checking what happened then to avoid making the same mistakes. You use past tags to confirm memories and details from that trip.`,

      questions: [
        {
          type: "match",
          q: "**Match past events → correct tags**",
          pairs: [
            { left: "You packed everything last night", right: "didn't you?" },
            { left: "We didn't book activities", right: "did we?" },
            { left: "The hotel confirmed by email", right: "didn't it?" },
            { left: "They lost our luggage", right: "didn't they?" }
          ],
          successTip: "🕐 **Past pattern locked!** All past simple uses did/didn't in tags!"
        },
        {
          type: "mc",
          q: "**You:** 'Last year, we forgot sunscreen, ___ ?'",
          options: ["didn't we", "did we", "don't we", "weren't we"],
          a: "didn't we",
          successTip: "☀️ **Right!** 'forgot' (past) → 'didn't we?' Learning from mistakes!"
        },
        {
          type: "mc",
          q: "**Roommate:** 'You bought travel adapters, ___ ?'",
          options: ["didn't you", "did you", "don't you", "haven't you"],
          a: "didn't you",
          successTip: "🔌 **Perfect!** 'bought' (past) → 'didn't you?' Confirmation works!"
        },
        {
          type: "input",
          q: "**You:** 'We didn't check baggage fees, ___ ___ ?'",
          alts: ["did we"],
          a: "did we",
          successTip: "💸 **Excellent!** Negative past 'didn't check' → positive 'did we?'"
        },
        {
          type: "mc",
          q: "**Roommate:** 'The weather app predicted rain, ___ ?'",
          options: ["didn't it", "did it", "doesn't it", "wasn't it"],
          a: "didn't it",
          successTip: "🌧️ **Correct!** 'predicted' (past verb) → 'didn't it?' Always works!"
        },
        {
          type: "mc",
          q: "**You:** 'You didn't pack your camera, ___ ?'",
          options: ["did you", "didn't you", "do you", "have you"],
          a: "did you",
          successTip: "📸 **Great!** Negative 'didn't pack' → positive 'did you?' Classic pattern!"
        },
        {
          type: "input",
          q: "**Roommate:** 'We arrived late last time, ___ ___ ?'",
          alts: ["didn't we"],
          a: "didn't we",
          successTip: "⏰ **Perfect!** 'arrived' (past) → 'didn't we?' Memory check!"
        },
        {
          type: "mc",
          q: "**You:** 'The taxi driver charged too much, ___ ?'",
          options: ["didn't he", "did he", "doesn't he", "wasn't he"],
          a: "didn't he",
          successTip: "🚕 **Right!** Third person past 'charged' → 'didn't he?'"
        },
        {
          type: "mc",
          q: "**Roommate:** 'You printed the tickets yesterday, ___ ?'",
          options: ["didn't you", "did you", "don't you", "haven't you"],
          a: "didn't you",
          successTip: "🎫 **Excellent!** 'printed' (past) → 'didn't you?' Preparation confirmed!"
        },
        {
          type: "order",
          q: "**Say:** 'Reservaste el hotel en línea, ¿verdad?'",
          words: ["You", "booked", "the", "hotel", "online", "didn't", "you"],
          extra: ["don't", "did", "weren't"],
          a: ["You", "booked", "the", "hotel", "online", "didn't", "you"],
          successTip: "🏨 **Perfecto!** 'booked' (past) → 'didn't you?' - standard confirmation!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // SET 3: At the Airport - BE Verb Present (B1)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u6-s3",
      title: "Set 3: Airport Check-in",
      icon: "🛫",
      context: `**SITUACIÓN:** You're at Montreal-Trudeau Airport checking in for your flight to Vancouver. The airline staff and you are confirming details about your booking, seat assignments, and gate information using BE verbs. Everything needs to be verified before boarding.`,

      questions: [
        {
          type: "match",
          q: "**Match BE statements → tags**",
          pairs: [
            { left: "Your flight is on time", right: "isn't it?" },
            { left: "We're boarding at gate 12", right: "aren't we?" },
            { left: "This isn't your bag", right: "is it?" },
            { left: "You're in economy class", right: "aren't you?" }
          ],
          successTip: "🔄 **BE pattern clear!** BE verbs repeat themselves in tags - no 'do/did' needed!"
        },
        {
          type: "mc",
          q: "**Staff:** 'Your seat is 24A, ___ ?'",
          options: ["isn't it", "doesn't it", "hasn't it", "wasn't it"],
          a: "isn't it",
          successTip: "💺 **Perfect!** 'is' (BE verb) → 'isn't it?' Direct repetition!"
        },
        {
          type: "mc",
          q: "**You:** 'The gate is upstairs, ___ ?'",
          options: ["isn't it", "doesn't it", "hasn't it", "won't it"],
          a: "isn't it",
          successTip: "⬆️ **Right!** Location with 'is' → 'isn't it?' Airport navigation!"
        },
        {
          type: "input",
          q: "**Staff:** 'You're traveling with carry-on only, ___ ___ ?'",
          alts: ["aren't you"],
          a: "aren't you",
          successTip: "🎒 **Great!** 'are traveling' → 'aren't you?' Present continuous works!"
        },
        {
          type: "mc",
          q: "**You:** 'These aren't overweight, ___ ?'",
          options: ["are they", "aren't they", "do they", "don't they"],
          a: "are they",
          successTip: "⚖️ **Excellent!** Negative 'aren't' → positive 'are they?' Weight check!"
        },
        {
          type: "mc",
          q: "**Staff:** 'I'm checking you in now, ___ ?'",
          options: ["aren't I", "am I", "don't I", "won't I"],
          a: "aren't I",
          successTip: "✅ **Special case!** 'I am' → 'aren't I?' (NOT 'amn't I') - British & global standard!"
        },
        {
          type: "input",
          q: "**You:** 'The boarding time is 10:30, ___ ___ ?'",
          alts: ["isn't it"],
          a: "isn't it",
          successTip: "🕥 **Perfect!** Time confirmation with 'is' → 'isn't it?'"
        },
        {
          type: "mc",
          q: "**Staff:** 'You're Canadian, ___ ?'",
          options: ["aren't you", "don't you", "isn't you", "haven't you"],
          a: "aren't you",
          successTip: "🍁 **Right!** Nationality check: 'are' → 'aren't you?'"
        },
        {
          type: "mc",
          q: "**You:** 'That's the final call, ___ ?'",
          options: ["isn't it", "doesn't it", "wasn't it", "hasn't it"],
          a: "isn't it",
          successTip: "📢 **Urgent!** 'That is' (contracted) → 'isn't it?' Last chance!"
        },
        {
          type: "order",
          q: "**Say:** 'Estamos en la puerta correcta, ¿verdad?'",
          words: ["We're", "at", "the", "right", "gate", "aren't", "we"],
          extra: ["don't", "isn't", "won't"],
          a: ["We're", "at", "the", "right", "gate", "aren't", "we"],
          successTip: "🚪 **Perfecto!** Contraction 'We're' (We are) → 'aren't we?'"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // SET 4: On the Plane - BE Verb Past (B1)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u6-s4",
      title: "Set 4: In-Flight Conversations",
      icon: "✈️",
      context: `**SITUACIÓN:** You're on the plane to Vancouver, sitting next to a friendly passenger. You're chatting about past travel experiences and comparing notes about previous flights, delays, and travel stories. You both use past BE tags to confirm shared experiences.`,

      questions: [
        {
          type: "match",
          q: "**Match past BE → tags**",
          pairs: [
            { left: "The security line was long", right: "wasn't it?" },
            { left: "You weren't nervous", right: "were you?" },
            { left: "That announcement was clear", right: "wasn't it?" },
            { left: "We were lucky with seats", right: "weren't we?" }
          ],
          successTip: "🕐 **Past BE mastered!** Was/were repeat in tags - simple pattern!"
        },
        {
          type: "mc",
          q: "**Passenger:** 'That takeoff was smooth, ___ ?'",
          options: ["wasn't it", "didn't it", "isn't it", "hasn't it"],
          a: "wasn't it",
          successTip: "🛫 **Perfect!** 'was' (past BE) → 'wasn't it?' Smooth flight!"
        },
        {
          type: "mc",
          q: "**You:** 'You were in Montreal for business, ___ ?'",
          options: ["weren't you", "didn't you", "don't you", "aren't you"],
          a: "weren't you",
          successTip: "💼 **Right!** 'were' (past) → 'weren't you?' Small talk skill!"
        },
        {
          type: "input",
          q: "**Passenger:** 'The weather wasn't great yesterday, ___ ___ ?'",
          alts: ["was it"],
          a: "was it",
          successTip: "🌧️ **Great!** Negative 'wasn't' → positive 'was it?'"
        },
        {
          type: "mc",
          q: "**You:** 'That movie on the screen was interesting, ___ ?'",
          options: ["wasn't it", "didn't it", "isn't it", "won't it"],
          a: "wasn't it",
          successTip: "🎬 **Excellent!** In-flight entertainment: 'was' → 'wasn't it?'"
        },
        {
          type: "mc",
          q: "**Passenger:** 'You weren't here last week, ___ ?'",
          options: ["were you", "weren't you", "did you", "didn't you"],
          a: "were you",
          successTip: "📅 **Correct!** Negative 'weren't' → positive 'were you?'"
        },
        {
          type: "input",
          q: "**You:** 'The captain was welcoming, ___ ___ ?'",
          alts: ["wasn't he"],
          a: "wasn't he",
          successTip: "👨‍✈️ **Perfect!** Third person 'was' → 'wasn't he?'"
        },
        {
          type: "mc",
          q: "**Passenger:** 'Your last flight wasn't delayed, ___ ?'",
          options: ["was it", "wasn't it", "did it", "didn't it"],
          a: "was it",
          successTip: "⏱️ **Right!** Negative past 'wasn't delayed' → 'was it?'"
        },
        {
          type: "mc",
          q: "**You:** 'They were serving lunch earlier, ___ ?'",
          options: ["weren't they", "didn't they", "don't they", "aren't they"],
          a: "weren't they",
          successTip: "🍽️ **Great!** 'were serving' (past continuous) → 'weren't they?'"
        },
        {
          type: "order",
          q: "**Say:** 'Ese despegue fue aterrador, ¿verdad?'",
          words: ["That", "takeoff", "was", "scary", "wasn't", "it"],
          extra: ["didn't", "isn't", "weren't"],
          a: ["That", "takeoff", "was", "scary", "wasn't", "it"],
          successTip: "😰 **Perfecto!** 'was scary' → 'wasn't it?' - sharing feelings!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // SET 5: Hotel Arrival - Present Perfect Tags (B1)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u6-s5",
      title: "Set 5: Hotel Check-in Vancouver",
      icon: "🏨",
      context: `**SITUACIÓN:** You've just arrived at your Vancouver hotel after the flight. You're checking in at reception and the staff is confirming what has/hasn't been done regarding your reservation, room preparation, and any special requests you made online.`,

      questions: [
        {
          type: "match",
          q: "**Match present perfect → tags**",
          pairs: [
            { left: "You've checked in online", right: "haven't you?" },
            { left: "The room hasn't been cleaned", right: "has it?" },
            { left: "They've confirmed the booking", right: "haven't they?" },
            { left: "We've paid the deposit", right: "haven't we?" }
          ],
          successTip: "✅ **Perfect pattern!** Have/has repeats in tags - experience & completion!"
        },
        {
          type: "mc",
          q: "**Receptionist:** 'You've stayed with us before, ___ ?'",
          options: ["haven't you", "didn't you", "don't you", "aren't you"],
          a: "haven't you",
          successTip: "🔄 **Perfect!** 'have stayed' → 'haven't you?' Hotel loyalty check!"
        },
        {
          type: "mc",
          q: "**You:** 'You've prepared a non-smoking room, ___ ?'",
          options: ["haven't you", "didn't you", "don't you", "weren't you"],
          a: "haven't you",
          successTip: "🚭 **Right!** 'have prepared' → 'haven't you?' Important preference!"
        },
        {
          type: "input",
          q: "**Receptionist:** 'You haven't seen the gym yet, ___ ___ ?'",
          alts: ["have you"],
          a: "have you",
          successTip: "💪 **Great!** Negative 'haven't seen' → positive 'have you?' Amenity mention!"
        },
        {
          type: "mc",
          q: "**You:** 'The hotel has upgraded our room, ___ ?'",
          options: ["hasn't it", "didn't it", "doesn't it", "isn't it"],
          a: "hasn't it",
          successTip: "⭐ **Excellent!** 'has upgraded' → 'hasn't it?' Lucky you!"
        },
        {
          type: "mc",
          q: "**Receptionist:** 'Your friend hasn't arrived yet, ___ ?'",
          options: ["has she", "hasn't she", "did she", "didn't she"],
          a: "has she",
          successTip: "⏰ **Correct!** Negative 'hasn't arrived' → positive 'has she?'"
        },
        {
          type: "input",
          q: "**You:** 'We've requested early breakfast, ___ ___ ?'",
          alts: ["haven't we"],
          a: "haven't we",
          successTip: "🍳 **Perfect!** 'have requested' → 'haven't we?' Special request!"
        },
        {
          type: "mc",
          q: "**Receptionist:** 'You've been to Vancouver before, ___ ?'",
          options: ["haven't you", "didn't you", "weren't you", "aren't you"],
          a: "haven't you",
          successTip: "🇨🇦 **Right!** 'have been' → 'haven't you?' Experience question!"
        },
        {
          type: "mc",
          q: "**You:** 'The porter has taken our luggage, ___ ?'",
          options: ["hasn't he", "didn't he", "doesn't he", "isn't he"],
          a: "hasn't he",
          successTip: "🧳 **Great!** Third person 'has taken' → 'hasn't he?'"
        },
        {
          type: "order",
          q: "**Say:** 'Hemos pagado por adelantado, ¿verdad?'",
          words: ["We've", "paid", "in", "advance", "haven't", "we"],
          extra: ["didn't", "don't", "weren't"],
          a: ["We've", "paid", "in", "advance", "haven't", "we"],
          successTip: "💳 **Perfecto!** 'We've paid' → 'haven't we?' Payment confirmation!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // SET 6: Planning Activities - Will/Won't Tags (B1+)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u6-s6",
      title: "Set 6: Vancouver Itinerary",
      icon: "🗺️",
      context: `**SITUACIÓN:** You're in your hotel room planning tomorrow's activities with your travel companion. You're discussing what you will and won't do, making predictions about weather, and confirming tomorrow's schedule using will/won't tags.`,

      questions: [
        {
          type: "match",
          q: "**Match future plans → tags**",
          pairs: [
            { left: "We'll visit Stanley Park", right: "won't we?" },
            { left: "You won't forget the camera", right: "will you?" },
            { left: "The tour will start early", right: "won't it?" },
            { left: "They'll pick us up", right: "won't they?" }
          ],
          successTip: "🔮 **Future mastered!** Will/won't repeats in tags - planning & predictions!"
        },
        {
          type: "mc",
          q: "**Companion:** 'We'll take the SeaBus tomorrow, ___ ?'",
          options: ["won't we", "will we", "don't we", "aren't we"],
          a: "won't we",
          successTip: "🚢 **Perfect!** 'will take' → 'won't we?' Activity confirmed!"
        },
        {
          type: "mc",
          q: "**You:** 'The weather will be sunny, ___ ?'",
          options: ["won't it", "will it", "doesn't it", "isn't it"],
          a: "won't it",
          successTip: "☀️ **Right!** Weather prediction: 'will be' → 'won't it?'"
        },
        {
          type: "input",
          q: "**Companion:** 'You won't sleep in late, ___ ___ ?'",
          alts: ["will you"],
          a: "will you",
          successTip: "⏰ **Great!** Negative 'won't sleep' → positive 'will you?' Gentle reminder!"
        },
        {
          type: "mc",
          q: "**You:** 'This restaurant will be expensive, ___ ?'",
          options: ["won't it", "will it", "doesn't it", "isn't it"],
          a: "won't it",
          successTip: "💰 **Excellent!** 'will be' → 'won't it?' Budget concern!"
        },
        {
          type: "mc",
          q: "**Companion:** 'You'll book the whale watching, ___ ?'",
          options: ["won't you", "will you", "don't you", "aren't you"],
          a: "won't you",
          successTip: "🐋 **Correct!** 'will book' → 'won't you?' Exciting activity!"
        },
        {
          type: "input",
          q: "**You:** 'We won't need jackets, ___ ___ ?'",
          alts: ["will we"],
          a: "will we",
          successTip: "🧥 **Perfect!** Negative 'won't need' → positive 'will we?'"
        },
        {
          type: "mc",
          q: "**Companion:** 'The tour guide will speak English, ___ ?'",
          options: ["won't she", "will she", "doesn't she", "isn't she"],
          a: "won't she",
          successTip: "🗣️ **Right!** 'will speak' → 'won't she?' Language check!"
        },
        {
          type: "mc",
          q: "**You:** 'We'll get back before dark, ___ ?'",
          options: ["won't we", "will we", "don't we", "aren't we"],
          a: "won't we",
          successTip: "🌆 **Great!** 'will get back' → 'won't we?' Safety planning!"
        },
        {
          type: "order",
          q: "**Say:** 'No nos perderemos, ¿verdad?'",
          words: ["We", "won't", "get", "lost", "will", "we"],
          extra: ["don't", "aren't", "can't"],
          a: ["We", "won't", "get", "lost", "will", "we"],
          successTip: "🧭 **Perfecto!** Double negative: 'won't get lost' → 'will we?'"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // SET 7: At a Restaurant - Can/Could Tags (B1+)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u6-s7",
      title: "Set 7: Dining in Gastown",
      icon: "🍽️",
      context: `**SITUACIÓN:** You're having dinner at a nice restaurant in Gastown, Vancouver's historic district. You and your companion are discussing abilities, possibilities, and making polite suggestions using can/could tags while ordering and chatting about the experience.`,

      questions: [
        {
          type: "match",
          q: "**Match ability/possibility → tags**",
          pairs: [
            { left: "You can speak French", right: "can't you?" },
            { left: "We can't order dessert yet", right: "can we?" },
            { left: "They could hear us", right: "couldn't they?" },
            { left: "You could try the salmon", right: "couldn't you?" }
          ],
          successTip: "💪 **Ability & possibility!** Can/could repeats - present & past ability!"
        },
        {
          type: "mc",
          q: "**Waiter:** 'You can see the harbor from here, ___ ?'",
          options: ["can't you", "can you", "don't you", "aren't you"],
          a: "can't you",
          successTip: "🌊 **Perfect!** 'can see' (ability) → 'can't you?' Beautiful view!"
        },
        {
          type: "mc",
          q: "**You:** 'We can share this appetizer, ___ ?'",
          options: ["can't we", "can we", "don't we", "won't we"],
          a: "can't we",
          successTip: "🍤 **Right!** 'can share' → 'can't we?' Good suggestion!"
        },
        {
          type: "input",
          q: "**Companion:** 'You can't eat shellfish, ___ ___ ?'",
          alts: ["can you"],
          a: "can you",
          successTip: "🦞 **Great!** Negative 'can't eat' → positive 'can you?' Allergy check!"
        },
        {
          type: "mc",
          q: "**Waiter:** 'You could have wine with dinner, ___ ?'",
          options: ["couldn't you", "could you", "can't you", "don't you"],
          a: "couldn't you",
          successTip: "🍷 **Excellent!** 'could have' (suggestion) → 'couldn't you?'"
        },
        {
          type: "mc",
          q: "**You:** 'They can't take credit cards, ___ ?'",
          options: ["can they", "can't they", "do they", "don't they"],
          a: "can they",
          successTip: "💳 **Correct!** Negative 'can't take' → positive 'can they?' Payment concern!"
        },
        {
          type: "input",
          q: "**Companion:** 'We could walk back to the hotel, ___ ___ ?'",
          alts: ["couldn't we"],
          a: "couldn't we",
          successTip: "🚶 **Perfect!** 'could walk' → 'couldn't we?' Possibility suggestion!"
        },
        {
          type: "mc",
          q: "**Waiter:** 'You couldn't finish that huge steak, ___ ?'",
          options: ["could you", "couldn't you", "can you", "can't you"],
          a: "could you",
          successTip: "🥩 **Right!** Negative 'couldn't finish' → positive 'could you?' Size surprise!"
        },
        {
          type: "mc",
          q: "**You:** 'You can recommend something, ___ ?'",
          options: ["can't you", "can you", "couldn't you", "could you"],
          a: "can't you",
          successTip: "👨‍🍳 **Great!** 'can recommend' → 'can't you?' Asking for help!"
        },
        {
          type: "order",
          q: "**Say:** 'Puedes ver las montañas desde aquí, ¿verdad?'",
          words: ["You", "can", "see", "the", "mountains", "can't", "you"],
          extra: ["couldn't", "don't", "aren't"],
          a: ["You", "can", "see", "the", "mountains", "can't", "you"],
          successTip: "🏔️ **Perfecto!** 'can see' → 'can't you?' - amazing Vancouver views!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // SET 8: Making Suggestions - Should/Would Tags (B2-)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u6-s8",
      title: "Set 8: Planning Tomorrow",
      icon: "💡",
      context: `**SITUACIÓN:** You're back in your hotel room after dinner, relaxing and discussing what you should or would do tomorrow. You're giving advice, making hypothetical suggestions, and using should/would tags to sound more tentative and polite.`,

      questions: [
        {
          type: "match",
          q: "**Match suggestions → tags**",
          pairs: [
            { left: "We should visit Granville Island", right: "shouldn't we?" },
            { left: "You wouldn't go alone", right: "would you?" },
            { left: "They should arrive soon", right: "shouldn't they?" },
            { left: "She would love this place", right: "wouldn't she?" }
          ],
          successTip: "💭 **Advice & hypotheticals!** Should/would for suggestions & imagining!"
        },
        {
          type: "mc",
          q: "**Companion:** 'We should book tickets in advance, ___ ?'",
          options: ["shouldn't we", "should we", "won't we", "don't we"],
          a: "shouldn't we",
          successTip: "🎟️ **Perfect!** 'should book' (advice) → 'shouldn't we?' Smart planning!"
        },
        {
          type: "mc",
          q: "**You:** 'You wouldn't miss Capilano Bridge, ___ ?'",
          options: ["would you", "wouldn't you", "will you", "won't you"],
          a: "would you",
          successTip: "🌉 **Right!** Negative 'wouldn't miss' → positive 'would you?' Famous spot!"
        },
        {
          type: "input",
          q: "**Companion:** 'We should try poutine, ___ ___ ?'",
          alts: ["shouldn't we"],
          a: "shouldn't we",
          successTip: "🍟 **Great!** 'should try' → 'shouldn't we?' Canadian classic!"
        },
        {
          type: "mc",
          q: "**You:** 'That would be amazing, ___ ?'",
          options: ["wouldn't it", "would it", "won't it", "isn't it"],
          a: "wouldn't it",
          successTip: "🤩 **Excellent!** 'would be' (imagining) → 'wouldn't it?' Excitement!"
        },
        {
          type: "mc",
          q: "**Companion:** 'You shouldn't carry so much cash, ___ ?'",
          options: ["should you", "shouldn't you", "will you", "would you"],
          a: "should you",
          successTip: "💵 **Correct!** Negative 'shouldn't carry' → positive 'should you?' Safety advice!"
        },
        {
          type: "input",
          q: "**You:** 'We would save money with a day pass, ___ ___ ?'",
          alts: ["wouldn't we"],
          a: "wouldn't we",
          successTip: "🚇 **Perfect!** 'would save' → 'wouldn't we?' Budget smart!"
        },
        {
          type: "mc",
          q: "**Companion:** 'Your sister would enjoy this tour, ___ ?'",
          options: ["wouldn't she", "would she", "doesn't she", "isn't she"],
          a: "wouldn't she",
          successTip: "👩 **Right!** 'would enjoy' (imagining) → 'wouldn't she?' Family thought!"
        },
        {
          type: "mc",
          q: "**You:** 'We should check the weather first, ___ ?'",
          options: ["shouldn't we", "should we", "won't we", "can't we"],
          a: "shouldn't we",
          successTip: "⛅ **Great!** 'should check' (advice) → 'shouldn't we?' Practical!"
        },
        {
          type: "order",
          q: "**Say:** 'No deberíamos quedarnos hasta tarde, ¿verdad?'",
          words: ["We", "shouldn't", "stay", "out", "late", "should", "we"],
          extra: ["wouldn't", "won't", "don't"],
          a: ["We", "shouldn't", "stay", "out", "late", "should", "we"],
          successTip: "🌙 **Perfecto!** Negative advice: 'shouldn't stay' → 'should we?'"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // SET 9: WhatsApp Group - Mixed Tags (B2)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u6-s9",
      title: "Set 9: Travel Group Chat",
      icon: "💬",
      context: `**SITUACIÓN:** You're in a WhatsApp group chat with other travelers you met in Vancouver. Everyone is coordinating plans, sharing tips, and confirming details for a group hike tomorrow. Messages mix all types of question tags in rapid, natural conversation.`,

      questions: [
        {
          type: "mc",
          q: "**Alex:** 'Everyone's meeting at 8am, ___ ?' (present continuous)",
          options: ["aren't they", "don't they", "won't they", "haven't they"],
          a: "aren't they",
          successTip: "👥 **Perfect mix!** 'are meeting' → 'aren't they?' Group coordination!"
        },
        {
          type: "mc",
          q: "**Maria:** 'You've downloaded the trail map, ___ ?' (present perfect)",
          options: ["haven't you", "didn't you", "don't you", "aren't you"],
          a: "haven't you",
          successTip: "🗺️ **Right!** 'have downloaded' → 'haven't you?' Tech preparation!"
        },
        {
          type: "input",
          q: "**You:** 'The forecast looks good, ___ ___ ?' (present simple)",
          alts: ["doesn't it"],
          a: "doesn't it",
          successTip: "🌤️ **Great!** 'looks' (present) → 'doesn't it?' Weather check!"
        },
        {
          type: "mc",
          q: "**Jake:** 'We should bring snacks, ___ ?' (modal)",
          options: ["shouldn't we", "should we", "don't we", "won't we"],
          a: "shouldn't we",
          successTip: "🥪 **Excellent!** 'should bring' (advice) → 'shouldn't we?'"
        },
        {
          type: "mc",
          q: "**Sofia:** 'That hike was incredible last week, ___ ?' (past BE)",
          options: ["wasn't it", "didn't it", "isn't it", "hasn't it"],
          a: "wasn't it",
          successTip: "⛰️ **Correct!** 'was' (past BE) → 'wasn't it?' Shared memory!"
        },
        {
          type: "input",
          q: "**You:** 'Nobody's backing out now, ___ ___ ?' (negative)",
          alts: ["are they"],
          a: "are they",
          successTip: "🙅 **Perfect!** 'Nobody is' (negative meaning) → 'are they?' Commitment check!"
        },
        {
          type: "mc",
          q: "**Chen:** 'You won't forget sunscreen, ___ ?' (negative future)",
          options: ["will you", "won't you", "do you", "don't you"],
          a: "will you",
          successTip: "☀️ **Right!** 'won't forget' (negative) → 'will you?' Caring reminder!"
        },
        {
          type: "mc",
          q: "**Emma:** 'That waterfall is worth seeing, ___ ?' (present simple)",
          options: ["isn't it", "doesn't it", "wasn't it", "hasn't it"],
          a: "isn't it",
          successTip: "💧 **Great!** 'is worth' (BE) → 'isn't it?' Destination hype!"
        },
        {
          type: "mc",
          q: "**You:** 'We could carpool, ___ ?' (modal possibility)",
          options: ["couldn't we", "could we", "can't we", "can we"],
          a: "couldn't we",
          successTip: "🚗 **Excellent!** 'could carpool' → 'couldn't we?' Eco-friendly!"
        },
        {
          type: "order",
          q: "**Text:** 'Todo el mundo trae agua, ¿verdad?'",
          words: ["Everyone's", "bringing", "water", "aren't", "they"],
          extra: ["don't", "won't", "isn't"],
          a: ["Everyone's", "bringing", "water", "aren't", "they"],
          successTip: "💧 **Perfecto!** 'Everyone is bringing' → 'aren't they?' Essential item!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // SET 10: Farewell Conversation - Advanced Tags (B2+)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u6-s10",
      title: "Set 10: Last Day Reflections",
      icon: "👋",
      context: `**SITUACIÓN:** It's your last day in Vancouver. You're at a coffee shop reflecting on the trip with your travel companion, making complex statements with embedded clauses, discussing what could have been different, and confirming future plans with sophisticated tag usage.`,

      questions: [
        {
          type: "mc",
          q: "**You:** 'This has been the best trip ever, ___ ?' (superlative + present perfect)",
          options: ["hasn't it", "isn't it", "wasn't it", "didn't it"],
          a: "hasn't it",
          successTip: "⭐ **Advanced!** 'has been' (present perfect) → 'hasn't it?' Reflecting on experience!"
        },
        {
          type: "mc",
          q: "**Companion:** 'You'll miss the mountains, ___ ?' (future emotion)",
          options: ["won't you", "will you", "don't you", "wouldn't you"],
          a: "won't you",
          successTip: "🏔️ **Right!** 'will miss' → 'won't you?' Emotional prediction!"
        },
        {
          type: "input",
          q: "**You:** 'We could have stayed longer, ___ ___ ?' (past modal possibility)",
          alts: ["couldn't we"],
          a: "couldn't we",
          successTip: "⏱️ **Perfect!** Past modal 'could have stayed' → 'couldn't we?' Regret expressed!"
        },
        {
          type: "mc",
          q: "**Companion:** 'You're definitely coming back, ___ ?' (future intention)",
          options: ["aren't you", "don't you", "won't you", "wouldn't you"],
          a: "aren't you",
          successTip: "🔄 **Excellent!** 'are coming' (future in present) → 'aren't you?'"
        },
        {
          type: "mc",
          q: "**You:** 'Nothing went wrong this whole trip, ___ ?' (negative statement)",
          options: ["did it", "didn't it", "has it", "hasn't it"],
          a: "did it",
          successTip: "✅ **Correct!** 'Nothing went' (negative) → 'did it?' Perfect trip!"
        },
        {
          type: "input",
          q: "**Companion:** 'There's nothing like Vancouver, ___ ___ ?' (existential)",
          alts: ["is there"],
          a: "is there",
          successTip: "🌟 **Great!** 'There is nothing' (negative) → 'is there?' Strong opinion!"
        },
        {
          type: "mc",
          q: "**You:** 'Let's exchange contact info, ___ ?' (imperative + let's)",
          options: ["shall we", "should we", "will we", "do we"],
          a: "shall we",
          successTip: "📱 **Advanced!** 'Let's' → 'shall we?' British/formal but global in suggestions!"
        },
        {
          type: "mc",
          q: "**Companion:** 'You'd recommend this hotel to others, ___ ?' (conditional)",
          options: ["wouldn't you", "would you", "didn't you", "don't you"],
          a: "wouldn't you",
          successTip: "🏨 **Right!** 'You would' (contracted 'You'd') → 'wouldn't you?'"
        },
        {
          type: "mc",
          q: "**You:** 'There's no place we didn't visit, ___ ?' (double negative complexity)",
          options: ["is there", "isn't there", "did we", "didn't we"],
          a: "is there",
          successTip: "🗺️ **Complex!** 'There's no place' (negative) → 'is there?' Full exploration!"
        },
        {
          type: "order",
          q: "**Say:** 'No me olvidaré de esto, ¿verdad?'",
          words: ["I", "won't", "forget", "this", "will", "I"],
          extra: ["don't", "wouldn't", "can't"],
          a: ["I", "won't", "forget", "this", "will", "I"],
          successTip: "💭 **Perfecto!** Emotional: 'won't forget' → 'will I?' - memories forever!"
       }
      ]
    }
  ],


  readingSet: {
    id: "m9-u6-reading",
    title: "Reading: Vancouver Travel Blog",
    icon: "📖",
    context: `**SITUACIÓN:** You're reading a travel blog post by Sarah Chen, a Canadian traveler who just returned from Vancouver. She uses question tags throughout to engage readers. Read the passages and answer the questions.`,

    questions: [
      {
        type: "passage",
        text: `**My Amazing Week in Vancouver**

Vancouver is absolutely stunning, isn't it? I just got back from a week there, and I'm already planning my return trip. My flight wasn't delayed, which was lucky, wasn't it? I took the SkyTrain downtown after landing at YVR Airport.`
      },
      {
        type: "mc",
        q: "**1. Sarah's flight to Vancouver was delayed.**",
        options: ["True", "False", "Not mentioned"],
        a: "False",
        successTip: "✈️ She says 'My flight wasn't delayed, which was lucky, wasn't it?'"
      },
      {
        type: "mc",
        q: "**2. How did Sarah get downtown from the airport?**",
        options: ["Taxi", "Uber", "SkyTrain", "Bus"],
        a: "SkyTrain",
        successTip: "🚇 'I took the SkyTrain downtown' - efficient public transport!"
      },

      // ─────────────────────────────────────────────────────────
      // PASSAGE 2 (45 palabras)
      // ─────────────────────────────────────────────────────────
      {
        type: "passage",
        text: `I checked into my hotel in Gastown – you've heard of Gastown, haven't you? It's Vancouver's oldest neighborhood with cobblestone streets. Stanley Park is massive, isn't it? I rented a bike and cycled the entire Seawall – about 10 kilometers. The views are breathtaking, aren't they?`
      },
      {
        type: "mc",
        q: "**3. Where did Sarah stay?**",
        options: ["Stanley Park", "Gastown", "Granville Island", "Kitsilano"],
        a: "Gastown",
        successTip: "🏨 'I checked into my hotel in Gastown' - historic area!"
      },
      {
        type: "mc",
        q: "**4. How long is the Stanley Park Seawall?**",
        options: ["About 5km", "About 10km", "About 15km", "About 20km"],
        a: "About 10km",
        successTip: "🚴 'cycled the entire Seawall – about 10 kilometers'"
      },

      // ─────────────────────────────────────────────────────────
      // PASSAGE 3 (48 palabras)
      // ─────────────────────────────────────────────────────────
      {
        type: "passage",
        text: `The next day I visited Granville Island Public Market. You wouldn't believe the fresh food selection, would you? Local salmon, artisan cheeses, fresh bread... amazing! The Capilano Suspension Bridge was my trip highlight. The bridge is 140 meters long and 70 meters above the river – pretty scary, isn't it?`
      },
      {
        type: "mc",
        q: "**5. What impressed Sarah at Granville Island?**",
        options: ["The artists", "The fresh food selection", "The boats", "The architecture"],
        a: "The fresh food selection",
        successTip: "🍽️ 'You wouldn't believe the fresh food selection, would you?'"
      },
      {
        type: "mc",
        q: "**6. How high is Capilano Bridge above the river?**",
        options: ["50 meters", "70 meters", "90 meters", "140 meters"],
        a: "70 meters",
        successTip: "🌉 '70 meters above the river' - 140m is the length, not height!"
      },

      // ─────────────────────────────────────────────────────────
      // PASSAGE 4 (42 palabras)
      // ─────────────────────────────────────────────────────────
      {
        type: "passage",
        text: `You should book tickets online in advance, shouldn't you? I waited 45 minutes because I didn't pre-book. I also did the Grouse Grind hike – they call it "Mother Nature's StairMaster," don't they? It's 2.9 kilometers straight uphill. Tough but worth it!`
      },
      {
        type: "mc",
        q: "**7. Sarah pre-booked her Capilano Bridge tickets.**",
        options: ["True", "False", "Not mentioned"],
        a: "False",
        successTip: "🎫 She waited 45 minutes BECAUSE she didn't pre-book!"
      },
      {
        type: "mc",
        q: "**8. What is the Grouse Grind's nickname?**",
        options: ["The Stairway to Heaven", "Mother Nature's StairMaster", "The Mountain Climb", "The Grind"],
        a: "Mother Nature's StairMaster",
        successTip: "⛰️ Direct quote - 'they call it Mother Nature's StairMaster, don't they?'"
      },

      // ─────────────────────────────────────────────────────────
      // PASSAGE 5 (35 palabras) - Final
      // ─────────────────────────────────────────────────────────
      {
        type: "passage",
        text: `Vancouver isn't cheap, is it? But it's worth every penny. The people are friendly, the food is amazing. You're probably adding Vancouver to your bucket list now, aren't you?

*Sarah Chen - Travel Blogger from Toronto*`
      },
      {
        type: "mc",
        q: "**9. According to Sarah, Vancouver is:**",
        options: ["Very cheap", "Moderately priced", "Expensive but worth it", "Overpriced"],
        a: "Expensive but worth it",
        successTip: "💰 'Vancouver isn't cheap, is it? But it's worth every penny.'"
      },
      {
        type: "mc",
        q: "**10. Where is Sarah Chen from?**",
        options: ["Vancouver", "Montreal", "Toronto", "Calgary"],
        a: "Toronto",
        successTip: "🍁 Bio says 'Sarah Chen - Travel Blogger from Toronto'"
      }
    ]
  },

  // ═══════════════════════════════════════════════════════════════
  // 🔄 REVIEW SET - Sin cambios
  // ═══════════════════════════════════════════════════════════════
  reviewSet: {
    id: "m9-u6-review",
    title: "Review: All Question Tags",
    icon: "🔄",
    context: `**SITUACIÓN:** You're back home in Longueuil after your Vancouver trip. You're video chatting with a friend who wants to hear all about your vacation. This conversation mixes ALL types of question tags you've learned - present simple, past, BE verbs, auxiliaries, and modals. Time to show mastery!`,

    questions: [
      {
        type: "mc",
        q: "**Friend:** 'You had an amazing time, ___ ?' (past simple)",
        options: ["didn't you", "don't you", "haven't you", "weren't you"],
        a: "didn't you",
        successTip: "🎉 **Past review!** 'had' (past) → 'didn't you?'"
      },
      {
        type: "mc",
        q: "**You:** 'The weather was perfect, ___ ?' (past BE)",
        options: ["wasn't it", "didn't it", "isn't it", "hasn't it"],
        a: "wasn't it",
        successTip: "☀️ **Past BE review!** 'was' → 'wasn't it?'"
      },
      {
        type: "mc",
        q: "**Friend:** 'You're planning to go back, ___ ?' (present continuous)",
        options: ["aren't you", "don't you", "won't you", "haven't you"],
        a: "aren't you",
        successTip: "🔄 **BE verb review!** 'are planning' → 'aren't you?'"
      },
      {
        type: "mc",
        q: "**You:** 'Vancouver has great food, ___ ?' (present simple)",
        options: ["doesn't it", "hasn't it", "isn't it", "wasn't it"],
        a: "doesn't it",
        successTip: "🍽️ **Present simple review!** 'has' (verb) → 'doesn't it?'"
      },
      {
        type: "mc",
        q: "**Friend:** 'You haven't shown me all your photos, ___ ?' (present perfect)",
        options: ["have you", "haven't you", "did you", "didn't you"],
        a: "have you",
        successTip: "📸 **Auxiliary review!** Negative 'haven't shown' → positive 'have you?'"
      },
      {
        type: "mc",
        q: "**You:** 'I should send you the link, ___ ?' (modal)",
        options: ["shouldn't I", "should I", "don't I", "won't I"],
        a: "shouldn't I",
        successTip: "📲 **Modal review!** 'should send' → 'shouldn't I?'"
      },
      {
        type: "mc",
        q: "**Friend:** 'You didn't want to leave, ___ ?' (negative past)",
        options: ["did you", "didn't you", "do you", "don't you"],
        a: "did you",
        successTip: "😢 **Negative past review!** 'didn't want' → 'did you?'"
      },
      {
        type: "mc",
        q: "**You:** 'The mountains were breathtaking, ___ ?' (past BE)",
        options: ["weren't they", "didn't they", "aren't they", "don't they"],
        a: "weren't they",
        successTip: "🏔️ **Past BE plural!** 'were' → 'weren't they?'"
      },
      {
        type: "mc",
        q: "**Friend:** 'You'll share your itinerary with me, ___ ?' (future)",
        options: ["won't you", "will you", "don't you", "wouldn't you"],
        a: "won't you",
        successTip: "🗓️ **Future review!** 'will share' → 'won't you?'"
      },
      {
        type: "mc",
        q: "**You:** 'You can see why I loved it, ___ ?' (modal ability)",
        options: ["can't you", "can you", "don't you", "couldn't you"],
        a: "can't you",
        successTip: "❤️ **Can review!** 'can see' → 'can't you?' Sharing enthusiasm!"
      },
      {
        type: "mc",
        q: "**Friend:** 'We could plan a trip together, ___ ?' (modal possibility)",
        options: ["couldn't we", "could we", "can't we", "won't we"],
        a: "couldn't we",
        successTip: "👥 **Could suggestion!** 'could plan' → 'couldn't we?' Future plans!"
      },
      {
        type: "mc",
        q: "**You:** 'That would be incredible, ___ ?' (hypothetical)",
        options: ["wouldn't it", "would it", "won't it", "isn't it"],
        a: "wouldn't it",
        successTip: "🤩 **Would enthusiasm!** 'would be' → 'wouldn't it?'"
      },
      {
        type: "mc",
        q: "**Friend:** 'You bought souvenirs, ___ ?' (past simple)",
        options: ["didn't you", "did you", "don't you", "haven't you"],
        a: "didn't you",
        successTip: "🎁 **Past action!** 'bought' → 'didn't you?' Gift check!"
      },
      {
        type: "mc",
        q: "**You:** 'Let's look at flights together, ___ ?' (imperative let's)",
        options: ["shall we", "should we", "will we", "won't we"],
        a: "shall we",
        successTip: "✈️ **Advanced let's!** 'Let's' → 'shall we?' Planning together!"
      },
      {
        type: "mc",
        q: "**Friend:** 'This conversation has inspired me, ___ ?' (present perfect)",
        options: ["hasn't it", "didn't it", "doesn't it", "isn't it"],
        a: "hasn't it",
        successTip: "💭 **Perfect ending!** 'has inspired' → 'hasn't it?' Mission accomplished!"
      }
    ]
  }
};
